import { useEffect, useContext, useState } from "react";
import { useReport } from "../../contexts/ReportContext";
import { NavigationContext } from "../../contexts/NavigationContext";
import { useUpdate } from "../.././contexts/UpdateContext";
import { DESCRIPTION_MAX_LENGTH } from "../../constants/validation";
import { StyledButton } from "../../components/styles/buttons/Buttons";
import {
  StyledFormWrapper,
  StyledTextArea,
  DescriptionSpan,
  StyledLabel,
  StyledParagraph,
  StyledFormDescription,
  StyledError,
} from "../../components/styles/form/Form";
import {
  StyledHeroHeadingThin,
  StyledSpanWord,
  StyledBoldHeader,
  StyledInfoText,
} from "../../components/styles/Typography/Typography";
import {
  StyledButtonOuter,
  StyledButtonInner,
  StyledHeroContainer,
} from "../../components/styles/containers/Containers";

const DescriptionView = () => {
  const { setCurrentViewHeading } = useUpdate();
  const { handelSetFormInfo, formState } = useReport();
  const { dispatch } = useContext(NavigationContext);
  const [showMissingError, setShowMissingError] = useState(false);
  const [showLengthError, setShowLengthError] = useState(false);
  const REGISTRATION_TYPE = process.env.REACT_APP_REGISTRATION_TYPE;
  const descriptionValue = formState.description || "";
  // Keep a derived over-limit flag so UI and submit validation stay in sync.
  const isDescriptionTooLong = descriptionValue.length > DESCRIPTION_MAX_LENGTH;
  const shouldShowLengthError = showLengthError || isDescriptionTooLong;
  const shouldShowMissingError = showMissingError;
  const hasError = shouldShowMissingError || shouldShowLengthError;

  useEffect(() => {
    document.title = "Felanmälan - Beskriv ärendet";
    setCurrentViewHeading(
      <StyledHeroContainer>
        <StyledHeroHeadingThin>
          <StyledSpanWord>Beskriv&nbsp;ärendet</StyledSpanWord>
        </StyledHeroHeadingThin>
        {REGISTRATION_TYPE === "external" && (
          <StyledInfoText>
            Lämna endast de uppgifter som är nödvändiga för att vi ska kunna
            hantera ditt ärende. Ditt ärende blir en allmän handling som på
            begäran kan komma att lämnas ut. Det kan även komma att ges ut till
            andra aktörer som ska hantera ärendet.
          </StyledInfoText>
        )}
      </StyledHeroContainer>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFormInfo = (e) => {
    const value = e.target.value;
    handelSetFormInfo("description", value);
    setShowMissingError(false);
    // Clear length errors as soon as the input is back within the allowed size.
    if (value.length <= DESCRIPTION_MAX_LENGTH) {
      setShowLengthError(false);
    }
  };

  const handleNextClick = () => {
    const description = formState.description || "";

    // Treat whitespace-only input as missing required content.
    if (!description.trim()) {
      setShowMissingError(true);
      setShowLengthError(false);
      return;
    }

    // Defensive validation at submit time in addition to textarea maxLength.
    if (description.length > DESCRIPTION_MAX_LENGTH) {
      setShowLengthError(true);
      setShowMissingError(false);
      return;
    }

    dispatch({ type: "next" });
  };

  return (
    <>
      <StyledFormWrapper>
        <StyledFormDescription>
          <StyledParagraph>* = obligatoriskt</StyledParagraph>
          <StyledLabel htmlFor="Beskrivning">
            <StyledBoldHeader>
              Beskriv problemet eller din synpunkt
            </StyledBoldHeader>
            <DescriptionSpan> * </DescriptionSpan>
          </StyledLabel>
          <StyledTextArea
            id="Beskrivning"
            defaultValue={formState.description}
            onChange={handleFormInfo}
            maxLength={DESCRIPTION_MAX_LENGTH}
            aria-label="Fältet är obligatoriskt"
            aria-invalid={hasError}
            aria-describedby="descriptionLength errorValueMissing errorValueTooLong"
          />
          <StyledParagraph id="descriptionLength">
            {descriptionValue.length}/{DESCRIPTION_MAX_LENGTH} tecken
          </StyledParagraph>
          {shouldShowMissingError && (
            <StyledError
              aria-live="assertive"
              aria-describedby="Beskrivning"
              id="errorValueMissing"
            >
              * En beskrivning saknas
            </StyledError>
          )}
          {shouldShowLengthError && (
            <StyledError
              aria-live="assertive"
              aria-describedby="Beskrivning"
              id="errorValueTooLong"
            >
              * Beskrivningen får innehålla max {DESCRIPTION_MAX_LENGTH} tecken
            </StyledError>
          )}
        </StyledFormDescription>
      </StyledFormWrapper>
      <StyledButtonOuter>
        <StyledButtonInner>
          <StyledButton
            // disabled={!formState.description}
            onClick={() => {
              handleNextClick();
            }}
            aria-describedby="errorValueMissing errorValueTooLong"
          >
            Nästa steg
          </StyledButton>
          <StyledButton
            secondary
            onClick={() => {
              dispatch({ type: "previous" });
            }}
          >
            Tillbaka
          </StyledButton>
        </StyledButtonInner>
      </StyledButtonOuter>
    </>
  );
};

export default DescriptionView;
